// ---- a humanoid, read by name ----
// The four of them came out of the same pipeline, rigged the same way — Hips,
// LeftFoot, LeftToeBase and the rest, by name — each with a walk among the
// clips that came in the file. So what it takes to stand one of them in the
// field is the same for all four: turn the model to face +Z (the toes are
// ahead of the ankles), scale it to a height with its feet on the grass, make
// its material matte, move every clip's hips to where the rest pose's are so
// that a crossfade is not a slide, measure off the walk clip how far one cycle
// carries them, and measure off every clip how far above the ground it was
// authored. And what it takes to move one is the same too (Animator, below):
// one clip at a time, crossfaded from the last, the walk timed by the ground.
// jiddo.ts, tata.ts, boy.ts and baba.ts each had a copy of some of that,
// comments and all; this is the one copy. What is NOT here is what each of them
// does with it — which clip when, his wave and his startled turn, her orbit
// and her throw, the boy's dish in his hands — which stays in their own files.
import * as THREE from 'three';
import {fitHeight,footprint,isSkinned,posed} from './body';
import {elder,elderBody} from './field';

// The group the model stands in. Everything measured below comes back in ITS
// units, which is where the field's numbers live. This used to be `elder` by
// name, which was true of both grown-ups and false of the boy — he stands in a
// group of his own — and being the model's actual parent is what it always
// meant.
const groupOf=(m:THREE.Object3D)=>m.parent||m;

export interface Humanoid{hips:THREE.Object3D;toes:THREE.Object3D[];feet:THREE.Object3D[]}
const found=(b:THREE.Object3D|undefined):b is THREE.Object3D=>!!b;
// The model turned, scaled to `height` and set on the grass, its clips
// recentred, and the bones the rest of the loading reads by; null, with a
// warning naming the file, for a model that is not rigged this way.
export function readHumanoid(m:THREE.Object3D,height:number,clips:THREE.AnimationClip[],file:string,who='elder'):Humanoid|null{
  posed(m);
  const bone=(n:string)=>m.getObjectByName(n), hips=bone('Hips'), toes=['LeftToeBase','RightToeBase'].map(bone), feet=['LeftFoot','RightFoot'].map(bone);
  if(!hips||!toes.every(found)||!feet.every(found)){console.warn(file+': not the rig this expects (Hips, LeftFoot, LeftToeBase…)');return null;}
  // whichever way the rig faces, the group faces +Z: toes are ahead of ankles
  const wp=(o:THREE.Object3D)=>o.getWorldPosition(new THREE.Vector3());
  const fwd=wp(toes[0]).add(wp(toes[1])).sub(wp(feet[0])).sub(wp(feet[1])); fwd.y=0;
  if(fwd.length()>0.01)m.rotation.y=-Math.atan2(fwd.x,fwd.z);
  m.updateMatrixWorld(true); fitHeight(m,height);              // feet on the grass, as tall as asked
  m.traverse(o=>{if(isSkinned(o)){o.castShadow=true;o.frustumCulled=false;o.normalizeSkinWeights();o.userData.who=who;
    // The file's material is metal (glTF's default when the exporter says nothing) and fully emissive, which
    // under these lights is the texture unlit: flat, and no sun on them. So: plain matte, with a little of their
    // own colour unlit so the shaded side is not black — an ambient light on them alone, which three.js lights
    // cannot be told to be.
    const was=o.material as THREE.MeshStandardMaterial;
    o.material=new THREE.MeshStandardMaterial({map:was.map,emissiveMap:was.map,emissive:0xffffff,emissiveIntensity:0.3,roughness:1,metalness:0,side:THREE.DoubleSide});
  }});
  for(const c of clips)recentre(c,hips);
  return {hips,toes,feet};
}
// Every clip is authored about its own spot — Jiddo's wave stands a quarter of
// a unit to one side of the others — and a crossfade between two spots is a
// slide. So each clip's hips start where the rest pose's do, along the ground.
function recentre(clip:THREE.AnimationClip,hips:THREE.Object3D){
  const tr=clip.tracks.find(t=>t.name===hips.name+'.position'); if(!tr)return;
  const v=tr.values, dx=v[0]-hips.position.x, dz=v[2]-hips.position.z;
  for(let i=0;i<v.length;i+=3){v[i]-=dx;v[i+2]-=dz;}
}
// The grown-up stands: the model goes into the group the game put in the field,
// and the collisions are told how far their feet reach.
export function stand(m:THREE.Object3D){
  elder.add(m); elder.updateMatrixWorld(true); elderBody.pad=Math.max(0.45,footprint(elder));
}
// How far one cycle of a walk clip carries them, in the group's units (the
// model stands turned, scaled and in its group by now). A planted foot moves
// back at exactly the body's speed, so the clip is sampled along its length, and the toes'
// backward speed while they are at their lowest, over the cycle, is the
// stride — the median, since a landing and a lift-off are in there too.
// Measured off the clip rather than tuned by eye: the animals' CLIPSTRIDE is
// the by-eye version, and their feet still slide. `guess` is what a clip with
// no planted foot to read is given instead.
export function clipStride(mixer:THREE.AnimationMixer,clip:THREE.AnimationClip,toes:THREE.Object3D[],m:THREE.Object3D,guess:number){
  const N=60, d=clip.duration, a=mixer.clipAction(clip), p=new THREE.Vector3(), g=groupOf(m);
  a.play();
  const rows=toes.map(()=>[] as {y:number;z:number}[]);
  for(let k=0;k<=N;k++){
    mixer.setTime(k/N*d); g.updateMatrixWorld(true);
    toes.forEach((toe,i)=>{g.worldToLocal(toe.getWorldPosition(p));rows[i].push({y:p.y,z:p.z});});
  }
  a.stop(); mixer.setTime(0);
  const speeds:number[]=[];
  for(const r of rows){
    const lo=Math.min(...r.map(s=>s.y)), hi=Math.max(...r.map(s=>s.y)), floor=lo+0.15*(hi-lo);
    for(let k=0;k<N;k++)if(r[k].y<floor&&r[k+1].y<floor)speeds.push((r[k].z-r[k+1].z)*N/d);   // back is -z
  }
  speeds.sort((x,y)=>x-y);
  const v=speeds[speeds.length>>1]||0;
  if(v<0.05){console.warn(clip.name+': no planted foot found; a guess at its stride');return guess;}
  return v*d;
}
// How far off the ground each clip stands, in the group's units, by name.
// Every clip is authored about its own HEIGHT as well as its own spot: two of
// the boy's sit a couple of millimetres up and the third, Idle_11, sits seven
// centimetres up — which on a child 1.35 tall is a visible hover, with his
// shadow still on the grass underneath him. So the feet are sampled along each
// clip the way the stride is, the lowest they ever get is compared with the
// rest pose's, and the caller drops the model by the difference for as long as
// that clip is the one playing.
//
// Only ever a drop. A clip whose heel dips a few millimetres THROUGH the grass
// is a thing nobody has ever noticed; lifting the model to meet it would make a
// real float out of nothing.
//
// Call it before anything is played: the floor it measures against is the rest
// pose, which is what the mixer shows while no action is running.
export function clipLifts(mixer:THREE.AnimationMixer,clips:THREE.AnimationClip[],feet:THREE.Object3D[],m:THREE.Object3D){
  const N=24, p=new THREE.Vector3(), g=groupOf(m);
  const low=()=>{g.updateMatrixWorld(true);
    let lo=Infinity; for(const b of feet)lo=Math.min(lo,g.worldToLocal(b.getWorldPosition(p)).y);
    return lo;};
  const floor=low(), out:Record<string,number>={};
  for(const c of clips){
    const a=mixer.clipAction(c); a.play();
    let lo=Infinity;
    for(let k=0;k<=N;k++){mixer.setTime(k/N*c.duration);lo=Math.min(lo,low());}
    a.stop(); mixer.setTime(0);
    out[c.name]=Math.max(0,lo-floor);
  }
  return out;
}

// ---- the clips, one at a time ----
// What all four of them do with a mixer. One clip plays at a time, and a new
// one is crossfaded in from the last — `fade` seconds, which is each of theirs
// to choose — or, for a clip the file did not come with, the idle is (when
// there is one to fall back on). `once` plays it through and holds its last
// frame, for a clip that is a little scene with an end — his wave, the boy's
// mouthful — rather than a loop.
//
// A walk is timed by the ground, not by the clock: update() is handed how far
// into its cycle they are (`cycle`, in cycles, from the distance walked over
// the stride), and the clip is pinned there, so a planted foot never skates.
// Anything else plays on the clock.
//
// And mid-crossfade what shows is a weighting of two poses, so the height a
// clip was authored at (lifts, clipLifts) is that same weighting of the two —
// lift() — rather than a chase after the incoming one.
export class Animator{
  readonly mixer:THREE.AnimationMixer;
  private readonly byName:Record<string,THREE.AnimationClip>={};
  act:THREE.AnimationAction|null=null;
  name='';                                      // the clip playing
  was='';                                       // and the one it took over from
  constructor(readonly model:THREE.Object3D,private readonly all:THREE.AnimationClip[],private readonly idle?:string){
    this.mixer=new THREE.AnimationMixer(model);
    for(const c of all)this.byName[c.name]=c;
  }
  has(n:string){return !!this.byName[n];}
  clip(n:string):THREE.AnimationClip|undefined{return this.byName[n];}
  ms(n:string){const c=this.byName[n];return c?c.duration*1000:0;}   // in ms, like t
  // clipStride and clipLifts, above, on this model — once it is in its group,
  // since both measure in the group's units
  stride(n:string,toes:THREE.Object3D[],guess:number){const c=this.byName[n];return c?clipStride(this.mixer,c,toes,this.model,guess):guess;}
  lifts(feet:THREE.Object3D[]){return clipLifts(this.mixer,this.all,feet,this.model);}
  play(name:string,fade:number,once=false){
    const c=this.byName[name]||(this.idle?this.byName[this.idle]:undefined); if(!c)return;
    const a=this.mixer.clipAction(c);
    a.reset();
    if(once){a.setLoop(THREE.LoopOnce,1);a.clampWhenFinished=true;}
    else a.setLoop(THREE.LoopRepeat,Infinity);
    a.play();
    if(this.act&&this.act!==a)this.act.crossFadeTo(a,fade,false);
    this.act=a; this.was=this.name; this.name=name;
  }
  update(dt:number,cycle?:number){
    const a=this.act; if(!a)return;
    if(cycle===undefined)a.paused=false;
    else{a.paused=true;a.time=((cycle%1)+1)%1*a.getClip().duration;}   // timed by the ground, not by the clock
    this.mixer.update(dt);
  }
  lift(lifts:Record<string,number>){
    const w=this.act?this.act.getEffectiveWeight():1;
    return w*(lifts[this.name]||0)+(1-w)*(lifts[this.was]||0);
  }
}
